// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {noop} from '@github-ui/noop'
import type {SubIssueSidePanelItem} from '@github-ui/sub-issues/sub-issue-types'
import type {PreloadedQuery, UseQueryLoaderLoadQueryOptions} from 'react-relay/hooks'

import type {ItemIdentifier} from '../types/issue'
import type {IssueTimelineItemsItemType, IssueViewerViewQuery} from './__generated__/IssueViewerViewQuery.graphql'

export type IssueViewerPreloadedQueries = {
  issueViewerViewQuery: PreloadedQuery<IssueViewerViewQuery>
}

/**
 * Classic timeline — no tabs, all visible events shown in a single list.
 * This is the default when `tabbedTimeline` is omitted or set to false.
 */
type ClassicTimelineConfig = {
  tabbedTimeline?: false
  conversationEvents?: undefined
}

/**
 * Tabbed timeline — events are split into Conversation / Activity tabs.
 * `conversationEvents` is guaranteed to be a non-empty array when this mode
 * is active, so call sites can use it directly without null-checks.
 */
export type TabbedTimelineConfig = {
  tabbedTimeline: true
  /** Non-empty list of event types for the Conversation tab. */
  conversationEvents: IssueTimelineItemsItemType[]
}

/** Discriminated union — check `optionConfig.tabbedTimeline` to narrow. */
export type TimelineMode = ClassicTimelineConfig | TabbedTimelineConfig

type BaseOptionConfig = {
  /** function to (soft) navigate to a given url */
  navigate: (url: string) => void
  /** preloaded queries to use for the issue viewer - coming from the relay entrypoint */
  preloadedQueries?: IssueViewerPreloadedQueries
  /** function to be called when a link is clicked */
  onLinkClick?: (event: MouseEvent) => void
  /** function to be called when the close button in the actions toolbar is clicked */
  onClose?: () => void
  /** function to navigate back to the previous page */
  navigateBack?: () => void
  /** the graphQL id of the project to open in the sidebar */
  selectedProjectId?: string
  /** the owner of the projects to include in the sidebar */
  allowedProjectOwner?: string
  onRemovedFromProject: () => void
  showRepositoryPill?: boolean
  showIssueCreateButton?: boolean
  customEditMenuEntries?: React.ReactElement[]
  shouldSkipSetDocumentTitle?: boolean
  /** Fetching policies for the queries related to the issue number */
  issueQueriesFetchingPolicy?: UseQueryLoaderLoadQueryOptions
  /** callback when there was an update to the issue (except adding a comment) */
  onIssueUpdate: () => void
  /** callback when a new comment was added to the issue */
  onNewIssueComment?: () => void
  timelineEventBaseUrl?: string
  /** callback when the issue was deleted */
  onIssueDelete?: (issueId: string) => void
  /** additional actions to render in the header menu */
  additionalHeaderActions?: React.ReactNode
  /**
   * Called when an edit for a comment is canceled (e.g., user cleared the comment textarea)
   * @param commentId A unique ID for the given comment edit - either a new comment or existing comment
   */
  onCommentEditCancel?: (commentId: string) => void
  /**
   * Called when an edit for a comment is started (e.g., user started typing in the comment box)
   * @param commentId A unique ID for the given comment edit - either a new comment or existing comment
   */
  onCommentEditStart?: (commentId: string) => void
  /**
   * Called whenever the issue edit state changes (e.g., start changing body, canceled editing body)
   * @param isEditing Whether or not the issue body is currently being edited
   */
  onIssueEditStateChange?: (isEditing: boolean) => void
  /** Whether clicking the issue title in the sticky header should scroll to the top of the page or navigate away */
  scrollToTopOnClick?: boolean
  /** Used to control the behavior of clicking on a sub-issue in the sub-issue list view i.e. open in side-panel */
  onSubIssueClick?: (subIssueItem: SubIssueSidePanelItem) => void
  /** Used to control the behavior of clicking on the parent issue in the issue header i.e. close the side-panel */
  onParentIssueActivate?: (e: React.MouseEvent | React.KeyboardEvent, parentItemIdentifier: ItemIdentifier) => boolean
  /**
   * If true, use CSS Viewport queries for responsive layout. If false, use JS width listener.
   * @note This assumes that the IssueViewer is mounted to the root of the app and not inside a container.
   */
  useViewportQueries?: boolean
  /**
   * If true, indicates that the issue-viewer is within the currently opened side-panel. This is done
   * so that we have a quick way to determine whether the component accessing this is inside
   * of the side-panel or not without the need to search the dom with something like `.contains()`.
   * If false, the issue-viewer is not inside the open side-panel.
   * If undefined, the side panel is not open.
   */
  insideSidePanel?: boolean
  /**
   * If true, the entire issue viewer is readonly:
   * - user cannot edit anything via the "..." menu
   * - the sidebar is readonly
   * - the comment box at the end of the timeline is not shown
   * - buttons to edit title, add reactions, etc. are not shown
   */
  isReadonly?: boolean
  /**
   * If true, the issue timeline and comment thread is not shown.
   */
  hideTimeline?: boolean
  /**
   * Called when the issue viewer has loaded and rendered the issue data.
   * Useful for performance tracking.
   */
  onLoad?: () => void
  /**
   * Called when the user presses j to navigate to the next item in the list.
   * Used for keyboard navigation in the side panel.
   */
  onNavigateToNextItem?: () => void
  /**
   * Called when the user presses k to navigate to the previous item in the list.
   * Used for keyboard navigation in the side panel.
   */
  onNavigateToPreviousItem?: () => void
}

export type OptionConfig = BaseOptionConfig & TimelineMode

export const ISSUE_VIEWER_DEFAULT_CONFIG: OptionConfig = {
  navigate: noop,
  navigateBack: noop,
  onIssueUpdate: noop,
  onNewIssueComment: noop,
  onRemovedFromProject: noop,
  preloadedQueries: undefined,
  onLinkClick: noop,
  onClose: noop,
  showRepositoryPill: undefined,
  selectedProjectId: undefined,
  allowedProjectOwner: undefined,
  customEditMenuEntries: undefined,
  shouldSkipSetDocumentTitle: false,
  issueQueriesFetchingPolicy: undefined,
  timelineEventBaseUrl: undefined,
  useViewportQueries: false,
  isReadonly: false,
  hideTimeline: false,
  tabbedTimeline: false,
}
